import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Col, Form, Row } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Trash2 } from 'lucide-react';
import userService from '../../app/services/user.service';
import Card from '../../UI/Card/Card';
import Graybg from '../../UI/Graybg';
import BtnWrapper from '../../UI/BtnWrapper';
import SecBTN from '../../UI/SecBTN';
import Loader from '../../UI/loader/loader';
import Toast from '../../UI/Toast/Toast';
import {
  asRecords, answerKind, buildCorrectionPayload, CORRECTION_TYPES,
  filterPublishedPapers, prepareQuestions, REASONS_BY_TYPE, subjectsFromPaper,
  uniqueCorrectionHistory, validateCorrection, visibleQuestionText,
} from './correctionUtils';
import { divisionOptions, targetIdsForDivision } from './divisionMapping';
import styles from './PaperCorrectionsReport.module.css';

const EMPTY_MASTERS = { center: [], session: [], target: [], stclass: [] };
const EMPTY_ENTRY = { session: '', center: [], target: [], stclass: '', paper: '', subject: '' };
const EMPTY_REPORT = { session: '', center: '', division: '', stclass: '', paper: '' };
const OPTION_LETTERS = 'ABCDEF'.split('').map((letter) => ({ value: letter, label: letter }));

const optionsFrom = (items, key, label) => items.map((item) => ({ value: String(item[key]), label: String(item[label] || '') }));
const chosen = (options, value) => options.find((option) => option.value === String(value)) || null;
const getUserId = () => {
  try { return JSON.parse(localStorage.getItem('user'))?.data?.user?.user_id || ''; }
  catch { return ''; }
};
const questionResponse = (response) => response?.data?.data;
const publishedResponse = (response) => asRecords(response?.data?.data);

const dateText = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-GB');
};
const isDeleted = (row) => row.is_deleted === 1 || row.is_deleted === '1' || Boolean(row.deleted_at);
const normalizedType = (row) => String(row.correction_type_id ?? row.type_id ?? '');
const normalizedFacultyId = (row) => String(row.faculty_id ?? row.user_id ?? row.created_by ?? '');
const normalizedSubjectId = (row) => String(row.sub_id ?? '');
const displayType = (row) => CORRECTION_TYPES.find((type) => type.value === normalizedType(row))?.label || String(row.correction_type_name || '-');
const displayReason = (row) => REASONS_BY_TYPE[normalizedType(row)]?.find(
  (reason) => reason.value === String(row.correction_reason_type_id)
)?.label || row.reason || row.correction_reason_type_name || '-';
const counts = (rows) => ({
  total: rows.length,
  answer: rows.filter((r) => normalizedType(r) === '1').length,
  question: rows.filter((r) => normalizedType(r) === '2').length,
  bonus: rows.filter((r) => normalizedType(r) === '3').length,
});
const sortedGroup = (rows, id, label) => Object.values(rows.reduce((groups, row) => {
  const key = String(id(row));
  if (!groups[key]) groups[key] = { id: key, label: label(row), rows: [] };
  groups[key].rows.push(row);
  return groups;
}, {})).sort((a, b) => String(a.label).localeCompare(String(b.label)));

const AnswerField = ({ question, value, onChange }) => {
  const kind = answerKind(question.type_id);
  if (kind === 'single' || kind === 'boolean') {
    const options = kind === 'single' ? OPTION_LETTERS : [{ value: 'True', label: 'True' }, { value: 'False', label: 'False' }];
    return <Select aria-label="New Answer" options={options} value={chosen(options, value)}
      onChange={(item) => onChange(item?.value || '')} placeholder="Select new answer" isClearable />;
  }
  if (kind === 'multiple') {
    const values = String(value || '').split(',').filter(Boolean);
    return <Select aria-label="New Answer" isMulti options={OPTION_LETTERS}
      value={OPTION_LETTERS.filter((option) => values.includes(option.value))}
      onChange={(items) => onChange((items || []).map((item) => item.value).sort().join(','))}
      placeholder="Select correct options" />;
  }
  const multiLine = ['subjective', 'matching', 'caseStudy', 'text'].includes(kind);
  const placeholders = {
    integer: 'Enter integer answer',
    matching: "Enter the matching answer using the paper's existing answer format",
    subjective: 'Enter the subjective answer',
    fill: 'Enter the fill-in answer',
    caseStudy: 'Enter the case-study answer',
    text: 'Enter new answer',
  };
  return <Form.Control aria-label="New Answer" as={multiLine ? 'textarea' : 'input'}
    rows={multiLine ? 2 : undefined} type={multiLine ? undefined : 'text'}
    value={value} onChange={(event) => onChange(event.target.value)}
    placeholder={placeholders[kind] || 'Enter new answer'} />;
};

const PaperCorrectionsReport = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'reports' ? 'reports' : 'entry');
  const [loading, setLoading] = useState(false);
  const [masters, setMasters] = useState(EMPTY_MASTERS);
  const [mastersLoading, setMastersLoading] = useState(true);
  const [mastersError, setMastersError] = useState(false);
  const [entry, setEntry] = useState(EMPTY_ENTRY);
  // Full published-paper list is requested once on page mount, regardless of filters.
  const [allEntryPapers, setAllEntryPapers] = useState([]);
  const [entryPapersLoading, setEntryPapersLoading] = useState(true);
  const [entryPapersError, setEntryPapersError] = useState(false);
  const [entryPapers, setEntryPapers] = useState([]);
  const [entryFiltering, setEntryFiltering] = useState(false);
  const [entryLimited, setEntryLimited] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [paperOpened, setPaperOpened] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [editing, setEditing] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [submittedKeys, setSubmittedKeys] = useState({});
  const [report, setReport] = useState(EMPTY_REPORT);
  const [reportPapers, setReportPapers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [reportLevel, setReportLevel] = useState(1);
  const [reportSubject, setReportSubject] = useState('');
  const [reportFaculty, setReportFaculty] = useState('');
  const requestSeq = useRef(0);
  const reportRequestSeq = useRef(0);
  const entryFetchSeq = useRef(0);
  const mounted = useRef(true);

  const loadEntryPublishedPapers = async () => {
    const sequence = ++entryFetchSeq.current;
    setEntryPapersLoading(true);
    setEntryPapersError(false);
    try {
      // Dedicated service (page=1, limit=900). Never changes Publish Paper pagination.
      const response = await userService.getCorrectionPublishedPaperList();
      if (!mounted.current || sequence !== entryFetchSeq.current) return;
      if (response?.data?.info === false) throw new Error('Published-paper API rejected the request.');
      const records = publishedResponse(response);
      setAllEntryPapers(records);
      setEntryLimited(records.length >= 900);
    } catch (error) {
      if (mounted.current && sequence === entryFetchSeq.current) {
        setAllEntryPapers([]);
        setEntryLimited(false);
        setEntryPapersError(true);
        toast.error('Unable to load published papers. Use Retry to try again.');
      }
    } finally {
      if (mounted.current && sequence === entryFetchSeq.current) setEntryPapersLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    // Both calls start immediately, without waiting for a filter selection.
    userService.getCommonList().then((res) => {
      if (mounted.current) setMasters({ ...EMPTY_MASTERS, ...(res?.data?.data || {}) });
    }).catch(() => {
      if (mounted.current) { setMastersError(true); toast.error('Unable to load filter master data.'); }
    }).finally(() => { if (mounted.current) setMastersLoading(false); });
    loadEntryPublishedPapers();
    return () => {
      mounted.current = false; requestSeq.current += 1; reportRequestSeq.current += 1; entryFetchSeq.current += 1;
    };
    // Fetch on page mount only; changing any of the four entry filters never calls the API.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessionOptions = optionsFrom(masters.session, 'session_id', 'session');
  const centerOptions = optionsFrom(masters.center, 'center_id', 'center');
  const targetOptions = optionsFrom(masters.target, 'target_id', 'target');
  const classOptions = optionsFrom(masters.stclass, 'class_id', 'class');
  const divisionList = divisionOptions(masters.target);
  const entryFiltersComplete = Boolean(entry.session && entry.center.length && entry.target.length && entry.stclass);
  const selectedPaper = entryPapers.find((paper) => String(paper.paper_id) === entry.paper);
  const subjectOptions = subjectsFromPaper(selectedPaper);
  const entryPaperOptions = entryPapers.map((paper) => ({
    value: String(paper.paper_id), label: `${paper.paper_name} (${paper.paper_id})`,
  }));
  const reportPaperOptions = reportPapers.map((paper) => ({ value: String(paper.paper_id), label: `${paper.paper_name} (${paper.paper_id})` }));
  const dirty = Object.keys(drafts).length > 0 || editDraft !== null;

  const resetOpenedPaper = () => {
    setQuestions([]); setPaperOpened(false); setDrafts({}); setEditing(null);
    setEditDraft(null); setSubmittedKeys({});
  };
  const changeEntry = (key, value) => {
    if (dirty && !window.confirm('Discard unsubmitted correction drafts and change filters?')) return;
    requestSeq.current += 1;
    resetOpenedPaper();
    if (['session', 'center', 'target', 'stclass'].includes(key)) {
      // Clear the dependent Paper/Subject selections; filtered options are recalculated
      // from the already-fetched published papers by the effect below.
      setEntryPapers([]);
      setEntry((prev) => ({ ...prev, [key]: value, paper: '', subject: '' }));
    } else if (key === 'paper') setEntry((prev) => ({ ...prev, paper: value, subject: '' }));
    else setEntry((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    // Dependency: all four filters, loaded master options and the cached API response.
    // Delaying to a separate task lets React paint the loader before filtering if slow.
    setEntryPapers([]);
    if (!entryFiltersComplete || mastersLoading || mastersError || entryPapersLoading || entryPapersError) {
      setEntryFiltering(false);
      return undefined;
    }
    setEntryFiltering(true);
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      setEntryPapers(filterPublishedPapers(allEntryPapers, entry, masters));
      setEntryFiltering(false);
    }, 16);
    return () => { cancelled = true; clearTimeout(timeoutId); };
    // Only entry source/filter changes cause re-filtering. Paper/Subject selection does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEntryPapers, masters, mastersLoading, mastersError, entryPapersLoading, entryPapersError,
    entry.session, entry.center, entry.target, entry.stclass]);

  const openPaper = async () => {
    if (!entry.paper || !entry.subject) { toast.error('Choose a Paper and Subject.'); return; }
    if (dirty && !window.confirm('Discard unsubmitted corrections and reopen the paper?')) return;
    const sequence = ++requestSeq.current;
    resetOpenedPaper(); setLoading(true);
    try {
      const response = await userService.getPublishPaperVeiw(entry.paper, getUserId());
      if (sequence !== requestSeq.current) return;
      const records = prepareQuestions(questionResponse(response), entry.subject);
      if (!records.length) { toast.error('No questions found for this paper and subject.'); return; }
      setQuestions(records); setPaperOpened(true);
    } catch (error) { toast.error('Unable to load paper questions.'); }
    finally { if (sequence === requestSeq.current) setLoading(false); }
  };

  const startCorrection = (question) => {
    if (submittedKeys[question._key]) return;
    if (editing === question._key) { setEditing(null); setEditDraft(null); return; }
    setEditing(question._key);
    setEditDraft(drafts[question._key]
      ? { ...drafts[question._key] }
      : { correction_type_id: '', correction_reason_type_id: '', new_answer: '' });
  };
  const updateDraft = (field, value) => setEditDraft((prev) => ({
    ...prev, [field]: value, ...(field === 'correction_type_id' ? { correction_reason_type_id: '', new_answer: '' } : {}),
  }));
  const saveCorrection = (question) => {
    const error = validateCorrection(editDraft, question);
    if (error) { toast.error(error); return; }
    setDrafts((prev) => ({ ...prev, [question._key]: { ...editDraft } }));
    setEditing(null); setEditDraft(null);
  };
  const deleteCorrection = (question) => {
    setDrafts((prev) => {
      const next = { ...prev }; delete next[question._key]; return next;
    });
    setEditing(null); setEditDraft(null);
  };

  const submitCorrections = async () => {
    const selected = questions.filter((q) => drafts[q._key] && !submittedKeys[q._key]);
    if (!selected.length) { toast.error('Save at least one correction before Submit.'); return; }
    for (const question of selected) {
      if (!question.user_id || question.qno == null || question.matrix_id == null) {
        toast.error('Question lacks faculty, original number, or matrix identity; submission blocked.'); return;
      }
      const validation = validateCorrection(drafts[question._key], question);
      if (validation) { toast.error(`Question ${question.qno}: ${validation}`); return; }
      // Old backend identifies correction by original qno and faculty, not matrix_id.
      if (questions.some((other) => other !== question &&
        String(other.user_id) === String(question.user_id) && String(other.qno) === String(question.qno))) {
        toast.error(`Original Q.No ${question.qno} occurs twice for the same faculty. Backend question identity must be upgraded before submission.`);
        return;
      }
    }
    if (!window.confirm(`Submit ${selected.length} saved corrections?`)) return;
    // One request per faculty protects the current legacy top-level created_by contract.
    // Each correction ALSO includes its own created_by as required by the new API contract.
    const byFaculty = sortedGroup(selected, (q) => q.user_id, (q) => q.faculty || q.user_id);
    setLoading(true);
    try {
      for (const group of byFaculty) {
        const groupedDrafts = Object.fromEntries(group.rows.map((q) => [q._key, drafts[q._key]]));
        const payload = buildCorrectionPayload(entry.paper, entry.subject, group.rows, groupedDrafts);
        payload.created_by = group.rows[0].user_id;
        const response = await userService.PaperCorrectionSubmit(payload);
        if (response?.status === false || response?.info === false || response?.success === false) {
          throw new Error(response?.message || 'Submission rejected.');
        }
        const keys = group.rows.map((q) => q._key);
        setSubmittedKeys((prev) => ({ ...prev, ...Object.fromEntries(keys.map((key) => [key, true])) }));
        setDrafts((prev) => { const next = { ...prev }; keys.forEach((key) => delete next[key]); return next; });
      }
      setEditing(null); setEditDraft(null);
      toast.success('Corrections submitted successfully.');
    } catch (error) {
      toast.error('Submission stopped. Earlier faculty batches may already be saved; check records before retrying.');
    } finally { setLoading(false); }
  };

  const changeReport = (key, value) => {
    reportRequestSeq.current += 1;
    setReport((prev) => ({
      ...prev, [key]: value, ...(['session', 'center', 'division', 'stclass'].includes(key) ? { paper: '' } : {}),
    }));
    if (['session', 'center', 'division', 'stclass'].includes(key)) setReportPapers([]);
    setReportData(null); setReportLevel(1); setReportSubject(''); setReportFaculty('');
  };
  const loadReportPapers = async () => {
    if (!report.session || !report.center || !report.division) {
      toast.error('Select Session, Center and Division first.'); return;
    }
    const sequence = ++reportRequestSeq.current;
    const targetIds = targetIdsForDivision(masters.target, report.division).map(String);
    const session = masters.session.find((v) => String(v.session_id) === report.session)?.session;
    const center = masters.center.find((v) => String(v.center_id) === report.center)?.center;
    const stclass = masters.stclass.find((v) => String(v.class_id) === report.stclass)?.class;
    setLoading(true);
    try {
      const response = await userService.getCorrectionPublishedPaperList();
      const paperRows = publishedResponse(response).filter((paper) =>
        String(paper.SESSION).trim() === String(session).trim() &&
        String(paper.center).split(/[,;|]/).some((v) => v.trim().toLowerCase() === String(center).toLowerCase()) &&
        (stclass ? String(paper.class).trim() === stclass : true) &&
        targetIds.some((id) => {
          const label = masters.target.find((t) => String(t.target_id) === id)?.target;
          return String(paper.target).split(/[,;|]/).some((value) => value.trim().toLowerCase() === String(label).toLowerCase());
        })
      );
      if (sequence !== reportRequestSeq.current) return;
      setReportPapers(paperRows);
      if (publishedResponse(response).length >= 900) toast.warn('Paper selector is limited to the first 900 published papers.');
    } catch (error) { if (sequence === reportRequestSeq.current) toast.error('Unable to load optional report papers.'); }
    finally { if (sequence === reportRequestSeq.current) setLoading(false); }
  };

  const searchReport = async () => {
    if (!report.session || !report.center || !report.division) {
      toast.error('Session, Center and Division are required.'); return;
    }
    const targetIds = targetIdsForDivision(masters.target, report.division);
    if (!targetIds.length) { toast.error('Approved Target to Division mapping is missing.'); return; }
    const sequence = ++reportRequestSeq.current;
    setReportData(null); setReportLevel(1); setLoading(true);
    try {
      // NEW BACKEND CONTRACT: filtered, non-deleted correction history with
      // paper_id, matrix_id, subject/faculty IDs, type and reason IDs.
      const response = await userService.getPaperCorrectionReport({
        session_id: report.session, center_id: report.center,
        division: report.division, target_ids: targetIds,
        class_id: report.stclass || null, paper_id: report.paper || null,
      });
      const payload = response?.data?.data || response?.data || {};
      const history = Array.isArray(payload) ? payload : payload.history;
      if (!Array.isArray(history)) throw new Error('Backend did not return a history array.');
      if (history.some((row) => row.matrix_id == null && row.correction_detail_id == null)) {
        throw new Error('History must include paper matrix_id or correction_detail_id to prevent counting ambiguities.');
      }
      if (sequence === reportRequestSeq.current) setReportData(uniqueCorrectionHistory(history.filter((row) => !isDeleted(row))));
    } catch (error) {
      if (sequence === reportRequestSeq.current) toast.error(error?.message || 'Correction reports API unavailable. Backend implementation is required.');
    } finally { if (sequence === reportRequestSeq.current) setLoading(false); }
  };

  // Keep optional Class/Paper limits in every drilldown, not just the summary.
  const scopedHistory = useMemo(() => (reportData || []).filter((row) => {
    // Defense-in-depth: preserve all Search filters at every drill-down level.
    if (row.session_id != null && String(row.session_id) !== report.session) return false;
    if (row.center_id != null && String(row.center_id) !== report.center) return false;
    if (row.target_id != null && !targetIdsForDivision(masters.target, report.division).some((id) => String(id) === String(row.target_id))) return false;
    if (report.stclass && row.class_id != null && String(row.class_id) !== report.stclass) return false;
    if (report.paper && String(row.paper_id) !== report.paper) return false;
    return !isDeleted(row);
  }), [reportData, report.session, report.center, report.division, report.stclass, report.paper, masters.target]);
  const subjectGroups = useMemo(() => sortedGroup(scopedHistory, normalizedSubjectId, (r) => r.sub_name || r.subject || `Subject ${r.sub_id}`), [scopedHistory]);
  const selectedSubjectGroup = subjectGroups.find((item) => item.id === reportSubject);
  const facultyGroups = selectedSubjectGroup ? sortedGroup(selectedSubjectGroup.rows, normalizedFacultyId, (r) => r.faculty_name || r.faculty || `Faculty ${normalizedFacultyId(r)}`) : [];
  const selectedFacultyGroup = facultyGroups.find((item) => item.id === reportFaculty);
  const contextText = [
    chosen(sessionOptions, report.session)?.label,
    chosen(centerOptions, report.center)?.label,
    report.division,
    chosen(classOptions, report.stclass)?.label,
    chosen(reportPaperOptions, report.paper)?.label,
  ].filter(Boolean).join(' › ');

  const exportWorkbook = () => {
    if (!reportData) return;
    const summary = subjectGroups.map((group) => {
      const c = counts(group.rows);
      return { Subject: group.label, Answer: c.answer, Question: c.question, Bonus: c.bonus, Total: c.total };
    });
    const faculty = subjectGroups.flatMap((subject) => sortedGroup(subject.rows, normalizedFacultyId, (r) => r.faculty_name || r.faculty || `Faculty ${normalizedFacultyId(r)}`)
      .map((group) => {
        const c = counts(group.rows);
        return { Subject: subject.label, Faculty: group.label, Answer: c.answer, Question: c.question, Bonus: c.bonus, Total: c.total };
      }));
    const history = scopedHistory.map((row) => ({
      Subject: row.sub_name || row.subject || '', Faculty: row.faculty_name || row.faculty || '',
      Class: row.class_name || row.class || '', 'Test / Paper': row.paper_name || '',
      'Paper ID': row.paper_id, 'Original Q.No': row.q_no ?? row.qno,
      'Matrix ID': row.matrix_id, 'Correction Type': displayType(row),
      Reason: displayReason(row), 'Old Answer': row.old_correction || '',
      'New Answer': row.new_correction || '', Date: dateText(row.created_on || row.created_at),
    }));
    const workbook = XLSX.utils.book_new();
    [['Subject Summary', summary], ['Faculty Summary', faculty], ['Correction History', history]].forEach(([name, data]) => {
      const worksheet = XLSX.utils.json_to_sheet(data.length ? data : [{ 'No records': '' }]);
      XLSX.utils.book_append_sheet(workbook, worksheet, name);
    });
    XLSX.writeFile(workbook, 'paper_corrections_report.xlsx');
  };

  return (
    <div className="section">
      {(loading || mastersLoading || (tab === 'entry' && (entryPapersLoading || entryFiltering))) && <Loader />}
      <Toast />
      <h4>Paper Corrections &amp; Report</h4>
      <Breadcrumb><Breadcrumb.Item active>Paper Corrections &amp; Report</Breadcrumb.Item></Breadcrumb>
      <Graybg>
        <BtnWrapper className={styles.tabs}>
          <SecBTN type="button" className={tab === 'entry' ? 'active' : ''} onClick={() => setTab('entry')}>Correction Entry</SecBTN>
          <SecBTN type="button" className={tab === 'reports' ? 'active' : ''} onClick={() => setTab('reports')}>Correction Reports</SecBTN>
        </BtnWrapper>
        {tab === 'entry' ? <>
          <Card className="mb-3">
            <h5>Select Paper</h5>
            <p className={styles.description}>Select Session, Center, Target and Class. Papers are automatically filtered from the published-paper list loaded when this page opens. Then select a Paper and Subject.</p>
            <Row className="g-3">
              <Col md={6}><Form.Label>Session *</Form.Label><Select options={sessionOptions} value={chosen(sessionOptions, entry.session)}
                onChange={(value) => changeEntry('session', value?.value || '')} placeholder="Select Session" isClearable /></Col>
              <Col md={6}><Form.Label>Center *</Form.Label><Select isMulti options={centerOptions}
                value={centerOptions.filter((v) => entry.center.includes(v.value))}
                onChange={(values) => changeEntry('center', (values || []).map((v) => v.value))} placeholder="Select Centers" /></Col>
              <Col md={6}><Form.Label>Target / Stream *</Form.Label><Select isMulti options={targetOptions}
                value={targetOptions.filter((v) => entry.target.includes(v.value))}
                onChange={(values) => changeEntry('target', (values || []).map((v) => v.value))} placeholder="Select Targets" /></Col>
              <Col md={6}><Form.Label>Class *</Form.Label><Select options={classOptions}
                value={chosen(classOptions, entry.stclass)} onChange={(value) => changeEntry('stclass', value?.value || '')}
                placeholder="Select Class" isClearable /></Col>
              <Col md={6}><Form.Label>Test Name / Paper ID *</Form.Label><Select options={entryPaperOptions}
                value={chosen(entryPaperOptions, entry.paper)} onChange={(value) => changeEntry('paper', value?.value || '')}
                isDisabled={!entryFiltersComplete || mastersLoading || mastersError || entryPapersLoading || entryPapersError || entryFiltering || !entryPapers.length}
                isLoading={entryFiltering} placeholder={entryPapersLoading ? 'Loading published papers...' : entryFiltering ? 'Filtering papers...' : !entryFiltersComplete ? 'Select the first four filters' : entryPapersError ? 'Unable to load published papers' : entryPapers.length ? 'Select Test / Paper' : 'No matching papers'}
                isSearchable isClearable /></Col>
              <Col md={6}><Form.Label>Subject *</Form.Label><Select options={subjectOptions}
                value={chosen(subjectOptions, entry.subject)} onChange={(value) => changeEntry('subject', value?.value || '')}
                isDisabled={!entry.paper || !subjectOptions.length} placeholder={entry.paper ? 'Select Subject' : 'Select Paper first'} isSearchable isClearable /></Col>
            </Row>
            {entryPapersError && <div className={styles.warning} role="alert">Published-paper data could not be loaded.
              <button type="button" className="btn btn-sm btn-outline-secondary ms-2" onClick={loadEntryPublishedPapers}>Retry Load</button>
            </div>}
            {!entryPapersError && (entryPapersLoading || mastersLoading || entryFiltering) &&
              <p className={styles.description} role="status">{entryPapersLoading ? 'Loading published papers...' : mastersLoading ? 'Loading filter options...' : 'Filtering papers...'}</p>}
            {!entryPapersLoading && !entryPapersError && !entryFiltering && entryFiltersComplete && !mastersLoading &&
              <p className={styles.description} role="status">{entryPapers.length} matching published paper{entryPapers.length === 1 ? '' : 's'} available.</p>}
            {entryLimited && <p className={styles.warning}>The service returned 900 papers. Results are limited to this first API page; additional pages require backend pagination confirmation.</p>}
            <div className={styles.actions}>
              <SecBTN type="button" onClick={openPaper} disabled={!entry.paper || !entry.subject || entryPapersLoading || entryFiltering || loading}>Open Paper</SecBTN>
            </div>
          </Card>
          {paperOpened && <Card className="mb-3">
            <h5>Question-wise Corrections — {chosen(subjectOptions, entry.subject)?.label} — {selectedPaper?.paper_name}</h5>
            <p className={styles.description}>Faculty grouping · sequential display numbers · original API question numbers preserved for submission. Corrections are local until final Submit.</p>
            <div className="table-responsive"><table className={`table table-bordered ${styles.questionTable}`}>
              <thead><tr><th>Q.No</th><th>Question (54 characters)</th><th>Faculty</th><th>Status</th><th>Action</th></tr></thead>
              <tbody>{questions.map((question, index) => {
                const previous = questions[index - 1];
                const newGroup = !previous || String(previous.user_id) !== String(question.user_id) || previous.faculty !== question.faculty;
                const saved = drafts[question._key];
                const submitted = submittedKeys[question._key];
                const open = editing === question._key;
                return <React.Fragment key={question._key}>
                  {newGroup && <tr className={styles.facultyGroup}><td colSpan={5}>Faculty: {question.faculty || `User ${question.user_id || 'unmapped'}`}</td></tr>}
                  <tr><td><strong>{index + 1}</strong><small className={styles.subNumber}>Original: {question.qno}</small></td>
                    <td title={visibleQuestionText(question.q_text, 10000)}>{visibleQuestionText(question.q_text)}</td>
                    <td>{question.faculty || '-'} <small className={styles.subNumber}>ID: {question.user_id || '-'}</small></td>
                    <td><span className={`${styles.status} ${submitted ? styles.submitted : saved ? styles.saved : ''}`}>
                      {submitted ? 'Submitted' : saved ? 'Correction saved' : 'No correction'}</span></td>
                    <td><button type="button" className="btn btn-sm btn-outline-secondary" disabled={Boolean(submitted)}
                      onClick={() => startCorrection(question)}>{open ? 'Close Editor' : saved ? 'Edit Correction' : '+ Add Correction'}</button></td></tr>
                  {open && editDraft && <tr className={styles.editRow}><td colSpan={5}>
                    <Row className="g-2 align-items-end">
                      <Col md={3}><Form.Label>Correction Type *</Form.Label><Select options={CORRECTION_TYPES}
                        value={chosen(CORRECTION_TYPES, editDraft.correction_type_id)}
                        onChange={(item) => updateDraft('correction_type_id', item?.value || '')} placeholder="Select Type" /></Col>
                      <Col md={3}><Form.Label>Reason *</Form.Label><Select options={REASONS_BY_TYPE[editDraft.correction_type_id] || []}
                        value={chosen(REASONS_BY_TYPE[editDraft.correction_type_id] || [], editDraft.correction_reason_type_id)}
                        onChange={(item) => updateDraft('correction_reason_type_id', item?.value || '')}
                        placeholder="Select Reason" isDisabled={!editDraft.correction_type_id} /></Col>
                      {editDraft.correction_type_id === '1' && <>
                        <Col md={2}><Form.Label>Old Answer</Form.Label><Form.Control value={String(question.answer ?? '')} readOnly /></Col>
                        <Col md={3}><Form.Label>New Answer *</Form.Label><AnswerField question={question} value={editDraft.new_answer}
                          onChange={(value) => updateDraft('new_answer', value)} /></Col>
                      </>}
                      <Col md={editDraft.correction_type_id === '1' ? 1 : 3} className={styles.editorActions}>
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => saveCorrection(question)}>Save</button>
                        {saved ? <button type="button" className="btn btn-sm btn-outline-danger" aria-label={`Delete correction for displayed question ${index + 1}`}
                          title="Delete saved correction" onClick={() => deleteCorrection(question)}><Trash2 size={16} /></button>
                          : <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setEditing(null); setEditDraft(null); }}>Cancel</button>}
                      </Col>
                    </Row>
                  </td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table></div>
            <div className={styles.actions}><span className={styles.description}>Saved drafts: {Object.keys(drafts).length} · Submitted this session: {Object.keys(submittedKeys).length}</span>
              <SecBTN type="button" disabled={!Object.keys(drafts).length || loading} onClick={submitCorrections}>Submit Corrections</SecBTN></div>
          </Card>}
        </> : <>
          <Card className="mb-3"><h5>Correction Summary</h5>
            <p className={styles.description}>Session, Center and Division required. Class and Test/Paper optional and retained at every reporting level.</p>
            {!divisionList.length && <p className={styles.warning}>Reports require the approved Target → Division mapping in divisionMapping.js. The supplied APIs do not define it.</p>}
            <Row className="g-3">
              <Col lg={4} md={6}><Form.Label>Session *</Form.Label><Select options={sessionOptions} value={chosen(sessionOptions, report.session)}
                onChange={(v) => changeReport('session', v?.value || '')} placeholder="Select Session" isClearable /></Col>
              <Col lg={4} md={6}><Form.Label>Center *</Form.Label><Select options={centerOptions} value={chosen(centerOptions, report.center)}
                onChange={(v) => changeReport('center', v?.value || '')} placeholder="Select Center" isClearable /></Col>
              <Col lg={4} md={6}><Form.Label>Division *</Form.Label><Select options={divisionList} value={chosen(divisionList, report.division)}
                onChange={(v) => changeReport('division', v?.value || '')} placeholder="Select Division" isClearable /></Col>
              <Col lg={4} md={6}><Form.Label>Class (optional)</Form.Label><Select options={classOptions} value={chosen(classOptions, report.stclass)}
                onChange={(v) => changeReport('stclass', v?.value || '')} placeholder="All Classes" isClearable /></Col>
              <Col lg={4} md={6}><Form.Label>Test / Paper (optional)</Form.Label><Select options={reportPaperOptions}
                value={chosen(reportPaperOptions, report.paper)} onChange={(v) => changeReport('paper', v?.value || '')}
                placeholder="All Papers" isClearable isSearchable /></Col>
            </Row>
            <div className={styles.actions}><button type="button" className="btn btn-sm btn-outline-secondary" onClick={loadReportPapers}
              disabled={!report.session || !report.center || !report.division}>Load Optional Papers</button>
              <SecBTN type="button" onClick={searchReport} disabled={!report.session || !report.center || !report.division}>Search</SecBTN></div>
          </Card>
          {reportData !== null && <>
            <p className={styles.context}>
              {reportLevel > 1 && <button type="button" className={styles.breadcrumbLink} onClick={() => setReportLevel(1)}>{contextText}</button>}
              {reportLevel === 1 && contextText}
              {reportLevel > 1 && ` › ${selectedSubjectGroup?.label || ''}`}
              {reportLevel > 2 && <><button type="button" className={styles.breadcrumbLink} onClick={() => setReportLevel(2)}>Faculty list</button> › {selectedFacultyGroup?.label || ''}</>}
            </p>
            {reportLevel === 1 && <>
              <div className={styles.stats}>{[
                ['Total Corrections', counts(scopedHistory).total],
                ['Answer Correction', counts(scopedHistory).answer],
                ['Question Correction', counts(scopedHistory).question],
                ['Bonus', counts(scopedHistory).bonus],
              ].map(([label, value]) => <Card className={styles.stat} key={label}><strong>{value}</strong><span>{label}</span></Card>)}</div>
              <Card className="mb-3"><div className={styles.headingRow}><h5>Breakdown by Subject</h5>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportWorkbook}>Download Excel (3 sheets)</button></div>
                <div className="table-responsive"><table className="table table-bordered"><thead><tr><th>Subject</th><th>Answer</th><th>Question</th><th>Bonus</th><th>Total</th><th>Action</th></tr></thead>
                  <tbody>{subjectGroups.map((group) => { const c = counts(group.rows); return <tr key={group.id}>
                    <td>{group.label}</td><td>{c.answer}</td><td>{c.question}</td><td>{c.bonus}</td><td>{c.total}</td>
                    <td><button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setReportSubject(group.id); setReportFaculty(''); setReportLevel(2); }}>View Detail</button></td>
                  </tr>; })}{!subjectGroups.length && <tr><td colSpan={6} className="text-center">No corrections found.</td></tr>}</tbody>
                </table></div></Card>
            </>}
            {reportLevel === 2 && <Card className="mb-3"><div className={styles.headingRow}><h5>Faculty-wise Corrections — {selectedSubjectGroup?.label}</h5>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportWorkbook}>Download Excel (3 sheets)</button></div>
              <div className="table-responsive"><table className="table table-bordered"><thead><tr><th>Faculty</th><th>Answer</th><th>Question</th><th>Bonus</th><th>Total</th></tr></thead>
                <tbody>{facultyGroups.map((group) => { const c = counts(group.rows); return <tr key={group.id}>
                  <td><button type="button" className={styles.breadcrumbLink} onClick={() => { setReportFaculty(group.id); setReportLevel(3); }}>{group.label}</button></td>
                  <td>{c.answer}</td><td>{c.question}</td><td>{c.bonus}</td><td>{c.total}</td></tr>; })}</tbody></table></div>
            </Card>}
            {reportLevel === 3 && <Card className="mb-3"><div className={styles.headingRow}><h5>Correction History — {selectedFacultyGroup?.label}</h5>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportWorkbook}>Download Excel (3 sheets)</button></div>
              <p className={styles.description}>All matching history within the selected Session, Center, Division, Class and Paper filters.</p>
              <div className={styles.miniSummary}>Answer: {counts(selectedFacultyGroup?.rows || []).answer} · Question: {counts(selectedFacultyGroup?.rows || []).question} · Bonus: {counts(selectedFacultyGroup?.rows || []).bonus} · Total: {counts(selectedFacultyGroup?.rows || []).total}</div>
              <div className="table-responsive"><table className="table table-bordered"><thead><tr><th>Class</th><th>Test / Paper</th><th>Original Q.No</th><th>Correction Type</th><th>Reason</th><th>Date</th></tr></thead>
                <tbody>{(selectedFacultyGroup?.rows || []).map((row, index) => <tr key={`${row.matrix_id || row.correction_detail_id}-${index}`}>
                  <td>{row.class_name || row.class || '-'}</td><td>{row.paper_name || row.paper_id || '-'}</td>
                  <td>{row.q_no ?? row.qno ?? '-'}</td><td>{displayType(row)}</td><td>{displayReason(row)}</td><td>{dateText(row.created_on || row.created_at)}</td>
                </tr>)}</tbody></table></div>
            </Card>}
          </>}
        </>}
      </Graybg>
    </div>
  );
};

export default PaperCorrectionsReport;
